/**
 * Connection path generation utilities
 * 
 * Generates arced paths from camera to cortex (or vice versa) with fixed curvature
 * but random angles for visual variety
 */

import type * as THREE_NS from 'three';

export interface PathGenerationOptions {
  THREE: typeof THREE_NS;
  camera?: THREE_NS.Camera;
  cortexPosition?: { x: number; y: number; z: number };
}

export type ConnectionType = 'microphone' | 'llm' | 'network';

// Conversational cortex position (entry point for all connections)
const CONVERSATIONAL_CORTEX_POSITION = { x: 0, y: 0, z: 0 };

// Curvature range for arcs (controls how "bent" the arc is)
// Higher values = more curved, lower values = less curved
const MIN_CURVATURE = 0.1; // 10% - nearly straight
const MAX_CURVATURE = 0.8; // 80% - very curved

// Random offset radius from camera position (so particles don't all originate from exact center)
const CAMERA_OFFSET_RADIUS = 15; // Units away from camera center

/**
 * Generate a unique arced path from camera to cortex (or vice versa) with varied curvature
 * and random angles for visual variety. Curves can be S-shaped, wide arcs, or nearly straight.
 */
export function generateConnectionPath(
  type: ConnectionType,
  options: PathGenerationOptions
): Array<{ x: number; y: number; z: number }> {
  const { THREE, camera, cortexPosition = CONVERSATIONAL_CORTEX_POSITION } = options;

  const { Vector3 } = THREE as any;
  
  // Get current camera position (always get fresh position, not cached)
  let cameraPos = { x: 0, y: 0, z: 50 }; // Default fallback
  if (camera) {
    // Update camera's world matrix to ensure we get current position
    camera.updateMatrixWorld(true);
    const camPos = new Vector3();
    camera.getWorldPosition(camPos);
    cameraPos = { x: camPos.x, y: camPos.y, z: camPos.z };
  }
  
  // Add random offset to camera position (so particles don't all originate from exact center)
  // Generate random point on sphere around camera
  const cameraOffset = new Vector3(
    (Math.random() - 0.5) * 2, // -1 to 1
    (Math.random() - 0.5) * 2,
    (Math.random() - 0.5) * 2
  ).normalize().multiplyScalar(CAMERA_OFFSET_RADIUS);
  
  const cameraPosVec = new Vector3(cameraPos.x, cameraPos.y, cameraPos.z);
  const offsetCameraPos = cameraPosVec.clone().add(cameraOffset);
  cameraPos = { x: offsetCameraPos.x, y: offsetCameraPos.y, z: offsetCameraPos.z };

  // Determine direction based on connection type:
  // - Incoming (to user): cortex → camera (llm responses)
  // - Outgoing (from user): camera → cortex (microphone, network requests)
  let startPos: { x: number; y: number; z: number };
  let endPos: { x: number; y: number; z: number };

  if (type === 'llm') {
    // LLM responses: incoming from cortex to camera
    startPos = cortexPosition;
    endPos = cameraPos;
  } else {
    // Microphone and network: outgoing from camera to cortex
    startPos = cameraPos;
    endPos = cortexPosition;
  }

  // Generate unique arced path with varied curvature and random angles
  const numPoints = 100; // Enough points for smooth animation
  const path: Array<{ x: number; y: number; z: number }> = [];
  
  // Calculate the straight-line vector from start to end
  const startVec = new Vector3(startPos.x, startPos.y, startPos.z);
  const endVec = new Vector3(endPos.x, endPos.y, endPos.z);
  const straightLine = new Vector3().subVectors(endVec, startVec);
  const distance = straightLine.length();
  const midpoint = new Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);
  
  // Random curvature amount (varies from nearly straight to very curved)
  const curvatureAmount = MIN_CURVATURE + Math.random() * (MAX_CURVATURE - MIN_CURVATURE);
  
  // Random angle for this arc (different for each particle)
  const randomAngle = Math.random() * Math.PI * 2; // Full 360 degrees
  const randomElevation = (Math.random() - 0.5) * Math.PI * 0.6; // ±54 degrees elevation (more variation)
  
  // Calculate perpendicular vector to the straight line for arc direction
  let perpendicular: Vector3;
  if (Math.abs(straightLine.y) < 0.9) {
    // If not too vertical, use Y-axis as reference
    perpendicular = new Vector3().crossVectors(straightLine, new Vector3(0, 1, 0)).normalize();
  } else {
    // If too vertical, use X-axis as reference
    perpendicular = new Vector3().crossVectors(straightLine, new Vector3(1, 0, 0)).normalize();
  }
  
  // Rotate perpendicular around the straight-line axis by random angle
  const rotationAxis = straightLine.clone().normalize();
  const Quaternion = (THREE as any).Quaternion;
  const rotationQuat = new Quaternion().setFromAxisAngle(rotationAxis, randomAngle);
  perpendicular.applyQuaternion(rotationQuat);
  
  // Apply elevation (tilt the arc up/down)
  const elevationAxis = new Vector3().crossVectors(perpendicular, rotationAxis).normalize();
  const elevationQuat = new Quaternion().setFromAxisAngle(elevationAxis, randomElevation);
  perpendicular.applyQuaternion(elevationQuat);
  
  // Calculate arc height (how far the arc bulges from the straight line)
  const arcHeight = distance * curvatureAmount;
  
  // Decide on curve type for more variety:
  // - 30% chance: S-curve (two control points, opposite directions)
  // - 40% chance: Simple arc (one control point)
  // - 30% chance: Wide arc (control point further out)
  const curveType = Math.random();
  
  if (curveType < 0.3) {
    // S-curve: two control points on opposite sides
    const controlPoint1 = midpoint.clone()
      .add(perpendicular.clone().multiplyScalar(arcHeight * 0.8))
      .add(straightLine.clone().normalize().multiplyScalar(-distance * 0.2)); // Offset along line
    const controlPoint2 = midpoint.clone()
      .add(perpendicular.clone().multiplyScalar(-arcHeight * 0.8))
      .add(straightLine.clone().normalize().multiplyScalar(distance * 0.2)); // Offset along line
    
    // Cubic Bezier: (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;
      const t2 = t * t;
      const t3 = t2 * t;
      
      const point = new Vector3()
        .addScaledVector(startVec, mt3)
        .addScaledVector(controlPoint1, 3 * mt2 * t)
        .addScaledVector(controlPoint2, 3 * mt * t2)
        .addScaledVector(endVec, t3);
      
      path.push({ x: point.x, y: point.y, z: point.z });
    }
  } else if (curveType < 0.7) {
    // Simple arc: single control point
    const controlPoint = midpoint.clone().add(perpendicular.clone().multiplyScalar(arcHeight));
    
    // Quadratic Bezier: (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;
      const mt = 1 - t;
      
      const point = new Vector3()
        .addScaledVector(startVec, mt * mt)
        .addScaledVector(controlPoint, 2 * mt * t)
        .addScaledVector(endVec, t * t);
      
      path.push({ x: point.x, y: point.y, z: point.z });
    }
  } else {
    // Wide arc: control point further out (more dramatic curve)
    const wideArcHeight = arcHeight * (1.5 + Math.random() * 0.5); // 1.5x to 2x normal height
    const controlPoint = midpoint.clone().add(perpendicular.clone().multiplyScalar(wideArcHeight));
    
    // Quadratic Bezier with wider arc
    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;
      const mt = 1 - t;
      
      const point = new Vector3()
        .addScaledVector(startVec, mt * mt)
        .addScaledVector(controlPoint, 2 * mt * t)
        .addScaledVector(endVec, t * t);
      
      path.push({ x: point.x, y: point.y, z: point.z });
    }
  }

  return path;
}

/**
 * Determine which pipe group a connection type should use
 * NOTE: With simplified paths, we don't need pipe groups anymore
 * This is kept for backward compatibility but returns null
 */
export function getPipeGroupForType(
  type: ConnectionType,
  options: PathGenerationOptions
): THREE_NS.Group | null {
  // No pipe groups needed for straight-line paths
  return null;
}
